import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import {
  AdditiveBlending,
  Color,
  Group,
  Mesh,
  MeshBasicNodeMaterial,
  MeshStandardMaterial,
  type Object3D,
} from 'three/webgpu';

import {
  BASE_ROTATION,
  COLORS,
  CONTROL_GROUPS,
  EMISSIVE_GAIN,
  FADER_LIMITS,
  KEY_DOWN_ROTATION,
} from '../core/constants';
import { midi } from '../midi/MidiBus';
import { useAppStore } from '../store/appStore';
import { noteOff, noteOn } from '../midi/generate';
import { MIDI_CHANNEL } from '../audio/transport';
import { meshNameToNote, useKeyboardStore } from '../store/keyboardStore';
import { panelActions, usePanelStore } from '../store/panelStore';
import { FADER_TO_KEY, type PanelCommand, type PanelFader } from '../store/panelTypes';
import { MODELS } from './assets';
import { createBurn } from './burn';
import { useInstrumentStore, type InstrumentRefs } from './instrumentStore';
import { createPulse } from './pulse';
import { useEffectPulses } from './useEffectPulses';
import type { ScreenTexture } from './screen/ScreenTexture';

const HIGHLIGHT = new Color(COLORS.pink);
/** Seconds the instrument takes to burn on, once the title card has cleared. */
const REVEAL_SECONDS = 1.4;
const PARALLAX = 0.1;
const PARALLAX_EASE = 6;
/** Local units the pointer drags a fader cap per pixel. */
const FADER_DRAG_SCALE = 0.01;
const FADER_TRAVEL = FADER_LIMITS.MAX - FADER_LIMITS.MIN;

const FADER_NAMES = Object.keys(FADER_TO_KEY) as PanelFader[];

function isFader(name: string): name is PanelFader {
  return (FADER_NAMES as string[]).includes(name);
}

/**
 * R3F invokes a handler once per intersection along the ray, so a pointer over
 * a key also reports the body behind it. Only the nearest hit is the one the
 * user means.
 */
function isNearestHit(event: ThreeEvent<PointerEvent>): boolean {
  return event.object === event.intersections[0]?.object;
}

/**
 * The VSS-30 itself.
 *
 * Hovering the keybed plays notes, exactly as the original did — the instrument
 * has no click-to-play, you sweep across it. Every note goes onto the shared
 * MIDI bus rather than straight to the synth, so a note played here is
 * indistinguishable downstream from one played on a controller and gets
 * captured by the loop recorder for free.
 */
export function Instrument({ screen, children }: { screen: ScreenTexture; children?: ReactNode }) {
  const { scene: model } = useGLTF(MODELS.instrument);
  const root = useRef<Group>(null);
  const overlays = useRef<Group>(null);
  const setRefs = useInstrumentStore((state) => state.setRefs);
  const clearRefs = useInstrumentStore((state) => state.clear);
  const setKeyboardLoaded = useAppStore((state) => state.setKeyboardLoaded);

  /** The note the pointer is currently resting on, if any. */
  const hoverNote = useRef<number | null>(null);
  const hoverButton = useRef<Mesh | null>(null);
  const dragging = useRef<{ cap: Object3D; fader: PanelFader } | null>(null);

  // Out of the point the title card collapsed into, so the instrument arrives
  // through the same hole it left by, and in the instrument's own teal rather
  // than the title card's ember: this one reads as powering on rather than
  // burning. The origin is set when the reveal starts, since the click that
  // decides it lands long after this is built.
  const burn = useMemo(
    () => createBurn({ radial: true, density: 40, growth: 2.6, colour: COLORS.teal, seed: 11 }),
    [],
  );

  // The ripple an effect sends across the panel when it is switched on: the
  // burn's lattice again, much tighter, lit rather than cut.
  const pulse = useMemo(() => createPulse({ seed: 23 }), []);

  const refs = useMemo<InstrumentRefs>(() => {
    const displayMaterial = new MeshBasicNodeMaterial();
    displayMaterial.colorNode = screen.node.rgb.mul(EMISSIVE_GAIN);
    displayMaterial.blending = AdditiveBlending;
    displayMaterial.transparent = true;
    displayMaterial.depthWrite = false;
    displayMaterial.toneMapped = false;

    const keys = new Map<number, Mesh>();
    const buttons = new Map<string, Mesh>();
    const faderCaps = new Map<PanelFader, Object3D>();

    model.traverse((child) => {
      if (!(child instanceof Mesh)) return;

      const material = child.material as MeshStandardMaterial;
      if (material?.name === 'Display') {
        child.material = displayMaterial;
        return;
      }

      // Every interactive mesh needs its own material so highlighting one
      // button does not light up its siblings.
      const note = meshNameToNote(child.name);
      const interactive = note !== null || child.name.startsWith('Button_') || isFader(child.name);
      if (interactive && material) {
        child.material = material.clone();
        child.userData.baseColor = (child.material as MeshStandardMaterial).color.clone();
      }

      if (note !== null) keys.set(note, child);
      else if (child.name.startsWith('Button_')) buttons.set(child.name, child);
      else if (isFader(child.name)) faderCaps.set(child.name, child);
    });

    const find = (name: string) => model.getObjectByName(name) ?? null;

    return {
      root: model as Group,
      keybed: find(CONTROL_GROUPS.KEYBED),
      controls: find(CONTROL_GROUPS.CONTROLS),
      faders: find(CONTROL_GROUPS.FADERS),
      displays: find(CONTROL_GROUPS.DISPLAYS),
      keys,
      buttons,
      faderCaps,
    };
  }, [model, screen]);

  // After the commit rather than during it, so the pair survives React mounting
  // this twice: the cleanup lets the materials go and the remount takes them
  // again, including the cloned ones the traverse above swaps in fresh each
  // time it runs.
  useLayoutEffect(() => {
    burn.applyTo(model);
    pulse.applyTo(model);
    return () => {
      pulse.dispose();
      burn.dispose();
    };
  }, [burn, pulse, model, refs]);

  useEffectPulses(pulse, refs.buttons);

  useEffect(() => {
    setRefs(refs);
    setKeyboardLoaded(true);
    return () => {
      clearRefs();
      setKeyboardLoaded(false);
    };
  }, [refs, setRefs, clearRefs, setKeyboardLoaded]);

  const releaseHoverNote = useCallback(() => {
    if (hoverNote.current === null) return;
    midi.send(noteOff(hoverNote.current, 0, MIDI_CHANNEL, 'pointer'));
    hoverNote.current = null;
  }, []);

  const setHoverButton = useCallback((mesh: Mesh | null) => {
    const previous = hoverButton.current;
    if (previous === mesh) return;
    if (previous) {
      const base = previous.userData.baseColor as Color | undefined;
      if (base) (previous.material as MeshStandardMaterial).color.copy(base);
    }
    if (mesh) (mesh.material as MeshStandardMaterial).color.copy(HIGHLIGHT);
    hoverButton.current = mesh;
  }, []);

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!isNearestHit(event)) return;
      const target = event.object;
      const note = meshNameToNote(target.name);

      if (note !== null) {
        if (hoverNote.current !== note) {
          releaseHoverNote();
          hoverNote.current = note;
          midi.send(noteOn(note, 100, MIDI_CHANNEL, 'pointer'));
        }
      } else {
        releaseHoverNote();
      }

      const highlightable =
        target instanceof Mesh && (target.name.startsWith('Button_') || isFader(target.name));
      setHoverButton(highlightable ? target : null);
      document.body.style.cursor = note !== null || highlightable ? 'pointer' : 'default';
    },
    [releaseHoverNote, setHoverButton],
  );

  // Fired whenever the pointer leaves any single mesh, including a move from
  // one key to the next; only an empty intersection list means it has actually
  // left the instrument.
  const onPointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.intersections.length > 0) return;
      releaseHoverNote();
      setHoverButton(null);
      document.body.style.cursor = 'default';
    },
    [releaseHoverNote, setHoverButton],
  );

  const onPointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
    if (!isNearestHit(event)) return;
    const name = event.object.name;
    if (isFader(name)) {
      // The drag continues on the window, so it survives the pointer leaving
      // the cap's own geometry.
      dragging.current = { cap: event.object, fader: name };
      return;
    }
    if (name.startsWith('Button_')) {
      panelActions.press(name);
      panelActions.run(name as PanelCommand);
    }
  }, []);

  const onPointerUp = useCallback((event: ThreeEvent<PointerEvent>) => {
    dragging.current = null;
    if (!isNearestHit(event)) return;
    const name = event.object.name;
    if (name.startsWith('Button_')) panelActions.release(name);
  }, []);

  // Fader dragging continues outside the cap's own geometry, so it listens on
  // the window rather than on the mesh.
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragging.current;
      if (!drag) return;
      const next = drag.cap.position.x + event.movementX * FADER_DRAG_SCALE;
      drag.cap.position.x = Math.min(FADER_LIMITS.MAX, Math.max(FADER_LIMITS.MIN, next));
      panelActions.setFader(drag.fader, (drag.cap.position.x - FADER_LIMITS.MIN) / FADER_TRAVEL);
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  useEffect(() => {
    return () => {
      releaseHoverNote();
      document.body.style.cursor = 'default';
    };
  }, [releaseHoverNote]);

  useFrame((state, delta) => {
    const group = root.current;
    if (!group) return;

    // Mounted while the title card is still burning away so the model parses
    // and the shaders compile behind it, but held at nothing until the card has
    // gone: the two reveals follow each other rather than crossing over.
    if (useAppStore.getState().phase === 'playing') {
      // Nothing is on screen at nothing, so the first frame of the reveal is
      // where the origin can be taken without anything jumping.
      if (burn.progress.value === 0) {
        const { x, y } = useAppStore.getState().burnOrigin;
        burn.origin.value.set(x, y);
      }
      burn.progress.value = Math.min(1, burn.progress.value + delta / REVEAL_SECONDS);
    }
    // The lit overlays belong to an instrument that is already there; they wait
    // for the reveal rather than floating over the half-burnt panel.
    if (overlays.current) overlays.current.visible = burn.progress.value >= 1;

    pulse.update(delta);

    // Parallax follows the pointer, eased so it glides rather than snaps.
    const ease = 1 - Math.exp(-PARALLAX_EASE * delta);
    const targetX = BASE_ROTATION + state.pointer.y * PARALLAX;
    const targetZ = state.pointer.x * PARALLAX;
    group.rotation.x += (targetX - group.rotation.x) * ease;
    group.rotation.z += (targetZ - group.rotation.z) * ease;

    const held = useKeyboardStore.getState().held;
    for (const [note, mesh] of refs.keys) {
      mesh.rotation.x = held.includes(note) ? KEY_DOWN_ROTATION : 0;
    }

    // A dragged cap is already at the pointer; anything else follows state.
    const panel = usePanelStore.getState();
    for (const [name, cap] of refs.faderCaps) {
      if (dragging.current?.cap === cap) continue;
      cap.position.x = FADER_LIMITS.MIN + FADER_TRAVEL * panel[FADER_TO_KEY[name]];
    }
  });

  return (
    <group ref={root} rotation-x={BASE_ROTATION}>
      <primitive
        object={model}
        onPointerMove={onPointerMove}
        onPointerOut={onPointerOut}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      />
      {/* Indicators are positioned from the model's own object positions, so
          they have to share its transform. */}
      <group ref={overlays} visible={false}>
        {children}
      </group>
    </group>
  );
}
